"use client"

import { useMemo, useState, type ReactNode } from "react"
import {
  SectionRule,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { readQuantity } from "@/lib/tools/numeric-input"

// ---------- Calc logic ----------
// Sources:
// - Manufacturer warranty structure: usually a standard parts + labor
//   warranty on the appliance, often with longer limited coverage on
//   sealed-system components (compressor, evaporator, condenser, dryer,
//   connecting tubing). Each maker sets its own lengths; the shopper checks
//   their own statement (FRIDGE-1250 — no brand's terms restated here).
// - Cost-of-repair benchmarks: refrigerator service-call + parts data
//   typically runs $200–$400 for common repairs (thermostat, fan, control
//   board), and $500–$1,200+ for sealed-system or compressor repairs.
//   Sources: published service-network rate sheets and major retailer
//   protection-plan disclosures.

interface Results {
  totalYears: number
  costPerYear: number
  costRatio: number
  rating: "good" | "fair" | "expensive" | "poor"
  ratingLabel: string
  expectedRepairValue: number
  netExpectedValue: number
}

// Heuristic expected-repair-value calculation:
// - p(repair in any year of extended period) ≈ 8% (industry rule of thumb;
//   conservative — some studies cite higher for high-feature units)
// - average covered repair cost ≈ $400 (mid-range of common repairs)
// Expected value = p × avg × extYears
function computeResults(
  price: number | null,
  warrantyCost: number | null,
  extYears: number | null,
  mfgYears: number | null,
): Results | null {
  // `=== null`, not `!`: the readers return null for anything unusable, and a
  // falsy test would additionally swallow a legitimate 0 while — before the
  // readers landed — letting every negative through.
  if (price === null || warrantyCost === null || extYears === null || mfgYears === null) return null

  const totalYears = mfgYears + extYears
  const costPerYear = warrantyCost / extYears
  const costRatio = (warrantyCost / price) * 100

  const expectedRepairValue = 0.08 * 400 * extYears
  const netExpectedValue = expectedRepairValue - warrantyCost

  let rating: Results["rating"]
  let ratingLabel: string
  if (costRatio < 10) {
    rating = "good"
    ratingLabel = "Good value"
  } else if (costRatio < 20) {
    rating = "fair"
    ratingLabel = "Fair value"
  } else if (costRatio < 30) {
    rating = "expensive"
    ratingLabel = "Expensive"
  } else {
    rating = "poor"
    ratingLabel = "Poor value"
  }

  return {
    totalYears,
    costPerYear,
    costRatio,
    rating,
    ratingLabel,
    expectedRepairValue,
    netExpectedValue,
  }
}

// ---------- Sides ----------

interface Side {
  key: "manufacturer" | "extended"
  name: string
  tag: string
  icon: string
  color: string
  pros: string[]
  cons: string[]
}

const SIDE_MFG: Side = {
  key: "manufacturer",
  name: "Manufacturer warranty only",
  tag: "Self-insure the rest",
  icon: "🏷",
  color: "#0469f0",
  pros: [
    "Already included in purchase — $0 extra",
    "A standard parts + labor warranty comes with the appliance",
    "Many models add longer limited coverage on sealed-system components — check yours",
    "Often transferable to next owner",
  ],
  cons: [
    "Coverage on non-sealed components usually ends with the standard warranty",
    "Out-of-warranty repairs are out-of-pocket",
    "Labor cost on long-tail sealed-system claims often excluded",
  ],
}

const SIDE_EXTENDED: Side = {
  key: "extended",
  name: "Extended protection plan",
  tag: "Buy peace of mind",
  icon: "🛡",
  color: "#ff9951",
  pros: [
    "Covers years 2–N when most non-sealed parts begin failing",
    "Often includes labor (sealed-system labor that brand warranty excludes)",
    "Some plans cover food-spoilage reimbursement",
    "Predictable monthly cost vs surprise repair bill",
  ],
  cons: [
    "Average plan ≈ 10–20% of appliance price — high for the actuarial expected value",
    "Most plans are non-transferable",
    "Exclusions: cosmetic damage, water damage, accidental damage usually not covered",
    "Many credit cards already extend manufacturer coverage by 1+ year for free",
  ],
}

interface Scenario {
  who: string
  winner: "manufacturer" | "extended"
  reason: string
}

const SCENARIOS: Scenario[] = [
  {
    who: "Premium-brand fridge ($2,000+) with 10-yr sealed warranty",
    winner: "manufacturer",
    reason: "The expensive failure modes (compressor, sealed system) are already covered for a decade. Self-insure the smaller stuff.",
  },
  {
    who: "Budget fridge under $1,000",
    winner: "manufacturer",
    reason: "Replacement is often cheaper than a major repair. Save the warranty premium toward the eventual replacement.",
  },
  {
    who: "Through-door ice/water dispenser model",
    winner: "extended",
    reason: "Dispenser components are the #1 refrigerator repair complaint. Extended plans typically cover them for years past the 1-yr brand window.",
  },
  {
    who: "Plan cost is over 20% of fridge price",
    winner: "manufacturer",
    reason: "At that ratio, the expected repair value almost never exceeds the premium. Pure profit for the seller.",
  },
  {
    who: "Cash-tight household, fridge is essential",
    winner: "extended",
    reason: "If a $600 surprise repair would derail your finances, the predictable plan cost may be worth the actuarial loss.",
  },
  {
    who: "You bought it on a card with extended-warranty coverage",
    winner: "manufacturer",
    reason: "Some credit cards extend the manufacturer warranty on eligible purchases. Check your card's benefits guide, then revisit.",
  },
]

interface Field {
  label: string
  hint?: string
  el: ReactNode
}

function InputField({ label, hint, el }: Field) {
  return (
    <div>
      <div className="mb-2">
        <label className="block text-[12px] font-bold uppercase tracking-[0.1em] text-gray-900">{label}</label>
        {hint && <span className="block text-[10px] text-gray-500 mt-0.5">{hint}</span>}
      </div>
      {el}
    </div>
  )
}

// ---------- Page ----------

export function WarrantyComparisonCalculatorPageClient() {
  const [price, setPrice] = useState("1500")
  const [warrantyCost, setWarrantyCost] = useState("180")
  const [extYears, setExtYears] = useState("3")
  const [mfgYears, setMfgYears] = useState("1")
  const [tab, setTab] = useState<"high" | "low">("high")

  // Bounds are the ones this tool already declared; only the OUT-OF-RANGE
  // behaviour changes. `safeParseNumber` CLAMPED — a typed `-1500` price came
  // back as `100`, which is truthy, so the `if (!price …)` guard in
  // computeResults could never fire and the panel answered confidently about a
  // $100 refrigerator. These readers reject instead, so a bad field shows no
  // answer rather than an answer to numbers nobody entered.
  // FRIDGE-384 (the guard) / FRIDGE-411 (the clamp).
  const priceNum = readQuantity(price, { min: 100, max: 50000 })
  const warrantyCostNum = readQuantity(warrantyCost, { min: 1, max: 10000 })
  const extYearsNum = readQuantity(extYears, { min: 1, max: 10 })
  const mfgYearsNum = readQuantity(mfgYears, { min: 1, max: 10 })

  const results = useMemo(
    () => computeResults(priceNum, warrantyCostNum, extYearsNum, mfgYearsNum),
    [priceNum, warrantyCostNum, extYearsNum, mfgYearsNum],
  )

  return (
    <ToolPageShell
      toolName="Refrigerator Warranty Comparison"
      heroEyebrow="Financial"
      heroTitlePre="Manufacturer warranty"
      heroTitleItalic="vs"
      heroTitlePost="extended plan."
      heroSubtitle="Many refrigerators already carry longer coverage on sealed-system components — check your own warranty. The question is whether the extended plan earns its premium for everything else — and the answer is usually no."
      heroMeta={["⏱ ~3 min read", "Check your own warranty statement"]}
      heroShare={<ToolShareBar title="Refrigerator Warranty Comparison" />}
      heroRight={
        <div className="rounded-xl p-6 bg-gray-50 border border-gray-200">
          <div className="flex items-start gap-3">
            <div
              className="font-serif text-[#ff9951] leading-none flex-shrink-0"
              style={{ fontSize: 40, letterSpacing: "-0.05em" }}
            >
              &ldquo;
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] font-bold mb-2 text-[#ff9951]">
                The short answer
              </div>
              <p
                className="font-medium text-gray-900"
                style={{
                  fontSize: 16,
                  lineHeight: 1.5,
                  letterSpacing: "-0.01em",
                  textWrap: "pretty" as never,
                }}
              >
                Skip the extended plan if it costs over{" "}
                <em className="italic text-[#ff9951] font-bold">10–15%</em> of the fridge price —
                the actuarial expected value rarely justifies it. Buy it for{" "}
                <em className="italic text-[#ff9951] font-bold">through-door dispensers</em> and
                tight budgets where a surprise repair would hurt.
              </p>
            </div>
          </div>
        </div>
      }
      faqs={[
        {
          question: "What does the manufacturer warranty actually cover?",
          answer:
            "Most refrigerators come with a standard parts-and-labor warranty, and many add longer limited coverage on sealed-system components — compressor, evaporator, condenser, dryer, and connecting refrigerant tubing. Each maker sets its own lengths, and labor on those long-tail sealed claims is often excluded. Read the printed warranty for your model.",
        },
        {
          question: "Why is the extended plan such a bad deal at 20%+ of price?",
          answer:
            "Refrigerators have a relatively low rate of in-warranty repair claims (industry studies cite roughly 7–10% per year for years 2–5). Multiply by an average covered repair cost in the $300–$500 range and the actuarial expected value of a 3-year plan is well under $150. Pricing above that is mostly retailer/insurer margin.",
        },
        {
          question: "When IS the extended plan worth it?",
          answer:
            "Three cases: (1) a through-door ice/water dispenser model, where the failure rate is materially higher; (2) a tight-cash household where a $600 surprise would force a high-interest loan; (3) credit-card-stacked coverage that already added 1 free year and the math now works.",
        },
        {
          question: "Do credit cards really extend warranty for free?",
          answer:
            "Some credit cards extend manufacturer warranties on eligible purchases. Coverage and exclusions are set by your card's benefits guide — check it before assuming.",
        },
        {
          question: "Is the manufacturer warranty transferable to a new owner?",
          answer:
            "Most major brand warranties are tied to the original purchaser and cannot be transferred. Most extended/protection plans explicitly cannot be transferred. Always verify with the printed terms — there are exceptions.",
        },
      ]}
      relatedTools={[
        { title: "Refrigerator Repair Cost Estimator", category: "Specialty", description: "Repair vs replace math.", href: "/tools/refrigerator-repair-cost-estimator", time: "~1 min" },
        { title: "Refrigerator Lifespan Calculator", category: "Specialty", description: "Years of life remaining.", href: "/tools/refrigerator-lifespan-calculator", time: "~45 sec" },
        { title: "Financing Comparison Tool", category: "Comparison", description: "Personal loan vs 0% promo.", href: "/tools/financing-comparison-tool", time: "~2 min" },
        { title: "Find Your Fridge", category: "Quiz", description: "Five questions to a starting product group.", href: "/tools/find-your-fridge", time: "~90 sec" },
      ]}
      cta={{
        title: "Ready to shop a long-warranty fridge?",
        body: "Browse bottom-freezer refrigerators — the lowest-failure-rate design with strong sealed-system warranties from major brands.",
        buttonLabel: "Browse bottom-freezer fridges",
        buttonHref: "/collections/bottom-freezer-refrigerators",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="py-8 md:py-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid lg:grid-cols-[460px_1fr] gap-8 lg:gap-12">
          <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 lg:sticky lg:top-24 lg:self-start">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-5 text-[#ff9951]">
              Run the numbers
            </div>
            <div className="space-y-6">
              <InputField
                label="Refrigerator price ($)"
                hint="Sticker price before tax"
                el={
                  <input type="number" min={100} max={50000} value={price} onChange={(e) => setPrice(e.target.value)}
                    className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold" />
                }
              />
              <InputField
                label="Extended plan cost ($)"
                hint="What the protection plan costs"
                el={
                  <input type="number" min={1} max={10000} value={warrantyCost} onChange={(e) => setWarrantyCost(e.target.value)}
                    className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold" />
                }
              />
              <div className="grid grid-cols-2 gap-3">
                <InputField
                  label="Mfg warranty (yrs)"
                  hint="Default = 1"
                  el={
                    <input type="number" min={1} max={10} value={mfgYears} onChange={(e) => setMfgYears(e.target.value)}
                      className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold" />
                  }
                />
                <InputField
                  label="Extended (yrs)"
                  hint="Years added"
                  el={
                    <input type="number" min={1} max={10} value={extYears} onChange={(e) => setExtYears(e.target.value)}
                      className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold" />
                  }
                />
              </div>
            </div>
          </div>

          <div>
            {results && (
              <>
                <div className="grid sm:grid-cols-2 gap-5 mb-6">
                  <div
                    className="rounded-xl p-6 border-2"
                    style={{
                      borderColor:
                        results.rating === "good"
                          ? "#16a34a"
                          : results.rating === "fair"
                          ? "#0469f0"
                          : "#ff9951",
                      background:
                        results.rating === "good"
                          ? "rgba(22,163,74,0.04)"
                          : results.rating === "fair"
                          ? "rgba(4,105,240,0.04)"
                          : "rgba(255,153,81,0.04)",
                    }}
                  >
                    <div
                      className="text-[10px] font-bold uppercase tracking-[0.15em] mb-1"
                      style={{
                        color:
                          results.rating === "good"
                            ? "#16a34a"
                            : results.rating === "fair"
                            ? "#0469f0"
                            : "#ff9951",
                      }}
                    >
                      Verdict
                    </div>
                    <div className="text-[24px] font-black tracking-tight text-gray-900 mb-2">{results.ratingLabel}</div>
                    <div className="text-[13px] text-gray-700">
                      Plan costs <strong>{results.costRatio.toFixed(1)}%</strong> of the fridge price.
                    </div>
                  </div>

                  <div className="rounded-xl p-6 border-2 border-gray-200">
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] mb-1 text-gray-500">
                      Expected vs paid
                    </div>
                    <div className="text-[24px] font-black tracking-tight text-gray-900 mb-2">
                      {results.netExpectedValue >= 0 ? "+" : ""}{`$${results.netExpectedValue.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                    </div>
                    <div className="text-[13px] text-gray-700">
                      Expected repair value (~{`$${results.expectedRepairValue.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}) minus what
                      you&apos;ll pay for the plan.
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 border-y border-gray-300 divide-x divide-gray-300 mb-6">
                  <div className="py-5 px-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">Total covered</div>
                    <div className="font-black text-gray-900" style={{ fontSize: 26, letterSpacing: "-0.03em" }}>
                      {results.totalYears} yrs
                    </div>
                  </div>
                  <div className="py-5 px-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">Cost / yr</div>
                    <div className="font-black text-gray-900" style={{ fontSize: 26, letterSpacing: "-0.03em" }}>
                      {`$${results.costPerYear.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                    </div>
                  </div>
                  <div className="py-5 px-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">Cost / fridge price</div>
                    <div className="font-black" style={{ fontSize: 26, letterSpacing: "-0.03em", color: "#ff9951" }}>
                      {results.costRatio.toFixed(1)}%
                    </div>
                  </div>
                </div>

                <p className="text-[13px] text-gray-500 leading-[1.5]">
                  Expected-value model assumes ~8% annual repair-claim rate during the extended
                  period and a $400 average covered repair cost. Adjust mentally for through-door
                  dispenser models (higher) or premium-brand sealed builds (lower).
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ============ SPEC FIGHT ============ */}
      <section className="bg-white py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="Side by side" title="The spec fight" right="Coverage, cost, transferability" />

          <div className="grid md:grid-cols-2 gap-5">
            {[SIDE_MFG, SIDE_EXTENDED].map((s, i) => (
              <div
                key={s.name}
                className="rounded-xl p-6 md:p-7"
                style={{
                  border: `2px solid ${s.color}`,
                  background: i === 0 ? "rgba(4,105,240,0.03)" : "rgba(255,153,81,0.03)",
                }}
              >
                <div className="flex items-center justify-between mb-4 pb-4 border-b" style={{ borderColor: `${s.color}30` }}>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em]" style={{ color: s.color }}>
                      {i === 0 ? "Side A" : "Side B"}
                    </div>
                    <div className="text-[24px] font-black tracking-tight text-gray-900">{s.name}</div>
                    <div className="text-[12px] text-gray-500 mt-0.5">{s.tag}</div>
                  </div>
                  <div className="text-3xl" style={{ color: s.color }}>{s.icon}</div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-green-700 mb-2">✓ Pros</div>
                    <ul className="space-y-1.5 text-[13px] text-gray-700">
                      {s.pros.map((p) => <li key={p} className="leading-[1.4]">{p}</li>)}
                    </ul>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-500 mb-2">✕ Cons</div>
                    <ul className="space-y-1.5 text-[13px] text-gray-500">
                      {s.cons.map((c) => <li key={c} className="leading-[1.4]">{c}</li>)}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SCENARIOS ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[960px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="Which wins for you?" title="Six scenarios, six verdicts" />
          <div className="border-t border-gray-900">
            {SCENARIOS.map((s, i) => (
              <div
                key={s.who}
                className="grid grid-cols-[auto_1fr] md:grid-cols-[auto_1fr_auto] gap-3 md:gap-5 items-start md:items-center py-4 md:py-5 border-b border-gray-900"
              >
                <div className="font-mono text-[13px] font-bold min-w-[28px] text-[#ff9951] pt-1 md:pt-0">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div>
                  <h3 className="text-[15px] md:text-[16px] font-extrabold tracking-[-0.015em] text-gray-900">{s.who}</h3>
                  <p className="text-[13px] text-gray-600 mt-0.5 leading-[1.5]">{s.reason}</p>
                </div>
                <div className="col-start-2 md:col-auto md:text-right mt-2 md:mt-0">
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold text-white text-[12px]"
                    style={{ background: s.winner === "manufacturer" ? "#0469f0" : "#ff9951" }}
                  >
                    {s.winner === "manufacturer" ? "Mfg only" : "Buy extended"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PRIMARY CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Shop the long-warranty side"
          title="Bottom-freezer refrigerators to compare"
          description="Bottom-freezer designs without a through-door dispenser have fewer parts that fail."
          collectionType="bottom-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-white py-8 md:py-12 border-t border-gray-200">
        <div className="max-w-[960px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="How we calculated" title="Methodology" />
          <div className="prose prose-sm max-w-none text-gray-700 space-y-3">
            <p>
              <strong>Manufacturer warranty terms.</strong> Refrigerators usually carry a
              standard parts-and-labor warranty, and many add longer limited coverage on the
              compressor or sealed system. Each maker sets its own lengths, and they differ between
              model lines, so enter the terms from your own warranty statement — coverage scope and
              labor inclusions vary by maker and model (FRIDGE-1250).
            </p>
            <p>
              <strong>Cost-ratio rating.</strong> &lt;10% of fridge price = good value, 10–20% =
              fair, 20–30% = expensive, 30%+ = poor. These thresholds reflect the relatively low
              actuarial value of refrigerator extended-protection plans across published
              consumer-protection studies and retailer disclosures.
            </p>
            <p>
              <strong>Expected-value model.</strong> Assumes ~8% annual probability of a covered
              repair claim during the extended period and a $400 average covered repair cost
              (mid-range of common refrigerator service-call benchmarks). Negative net value =
              the plan costs more than the expected payout. Adjust upward for through-door
              dispenser models (notably higher repair rates).
            </p>
            <p>
              <strong>What&apos;s not modeled.</strong> Food-spoilage reimbursement riders, accidental
              damage clauses, plan-specific deductibles, and credit-card extended-warranty stacks.
              Read the plan disclosure before buying.
            </p>
          </div>
        </div>
      </section>

      {/* ============ TABBED SECONDARY CAROUSEL ============ */}
      <section className="bg-white py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="Deep dive" title="Models to compare, by failure-rate profile" />
          <div className="flex gap-2 mb-6 flex-wrap">
            {(
              [
                ["high", "Higher-feature (more to break)"],
                ["low", "Simpler builds (fewer claims)"],
              ] as const
            ).map(([v, l]) => (
              <button
                key={v}
                type="button"
                onClick={() => setTab(v)}
                className={[
                  "px-5 py-2.5 rounded-full font-bold text-[12px] transition-all border border-gray-900",
                  tab === v ? "bg-gray-900 text-white" : "bg-white text-gray-900 hover:bg-gray-50",
                ].join(" ")}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <ToolProductCarousel
          key={tab}
          title={tab === "high" ? "Bottom-freezer fridges (an extended plan often pays)" : "Top-freezer fridges (skip the plan)"}
          collectionType={tab === "high" ? "bottom-freezer" : "top-freezer"}
          description={
            tab === "high"
              ? "Bottom-freezer designs with through-door dispensers have measurably higher repair rates — extended plans pencil out more often."
              : "Top-freezer designs are the lowest-failure-rate full-size category. Manufacturer warranty alone almost always wins the math."
          }
        />
      </section>
    </ToolPageShell>
  )
}
